import moment from 'moment';

import { FILTER_KEYS, FILTER_VALUES, QUERY_BEHAVIOR } from '../../constants';
import { minUtrValue, maxUtrValue } from '../../../../../utils/constants';
import { URL_DATE_FORMATS } from '../../../../../utils/dateFormats';

const {
  TYPE,
  EVENT_STATE,
  DATE_RANGE,
  START_DATE,
  END_DATE,
  UTR_MIN,
  UTR_MAX,
  UTR_TYPE,
  UTR_TEAM_TYPE,
  SPORT_TYPES,
  TOURS,
  EVENT_TYPES,
  EVENT_UTR_TYPES,
  EVENT_TEAM_TYPES,
  GENDERS,
  BALL_TYPES,
  LOCATION,
  LAT,
  LNG,
  DISTANCE,
  LOCATION_INPUT_VALUE,
  QUERY,
} = FILTER_KEYS;
const { EVENTS, OPEN_REGISTRATION, VERIFIED, SINGLES, DEFAULT_DISTANCE } = FILTER_VALUES;

const CSV_TO_BOOL_DICT_KEYS = new Set([
  TOURS,
  EVENT_TYPES,
  EVENT_UTR_TYPES,
  GENDERS,
  EVENT_TEAM_TYPES,
  BALL_TYPES,
  SPORT_TYPES,
]);

export const parseCsvIntoBooleanDict = (csv) => {
  const dict = {};
  if (!csv) {
    return dict;
  }
  csv.split(',').forEach(value => {
    dict[decodeURI(value)] = true;
  });
  return dict;
};

export const coerceLatLng = (value) => {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : null;
};

export const coerceUrlDate = (value) => {
  const m = moment(value, URL_DATE_FORMATS);
  return m.isValid() ? m : null;
};

export const parseUrlParamsToFilters = (parsed) => {
  const result = {};
  const hasQuery = Boolean(parsed?.query);

  Object.keys(parsed).forEach(key => {
    if (key === QUERY) {
      return;
    }
    if (key === EVENT_STATE && hasQuery) {
      return;
    }

    if (key === LOCATION) {
      result[LOCATION] = parsed[key];
      result[LOCATION_INPUT_VALUE] = parsed[key];
    } else if (CSV_TO_BOOL_DICT_KEYS.has(key)) {
      result[key] = parseCsvIntoBooleanDict(parsed[key]);
    } else if (key === UTR_MIN || key === UTR_MAX) {
      result[key] = parseInt(parsed[key], 10);
    } else if (key === LAT || key === LNG) {
      result[key] = coerceLatLng(parsed[key]);
    } else if (key === START_DATE || key === END_DATE) {
      result[key] = coerceUrlDate(parsed[key]);
    } else {
      result[key] = parsed[key];
    }
  });

  return result;
};

const buildSportTypes = (user, feedPreferences) => {
  if (user && feedPreferences) {
    return {
      tennis: feedPreferences?.showTennisContent ?? true,
      pickleball: feedPreferences?.showPickleballContent ?? true,
    };
  }
  return { tennis: true, pickleball: true };
};

const applyLocationFromUser = (baseFilters, user) => {
  if (!user?.location?.display) {
    return;
  }
  const userLocationLatLng = user.location.latLng || [];
  baseFilters[LOCATION] = user.location.display;
  baseFilters[DISTANCE] = DEFAULT_DISTANCE;
  baseFilters[LAT] = coerceLatLng(userLocationLatLng[0]);
  baseFilters[LNG] = coerceLatLng(userLocationLatLng[1]);
};

const hasAnyLocationParam = (parsed) => Boolean(
  parsed?.[LOCATION] ||
  parsed?.[LAT] ||
  parsed?.[LNG] ||
  parsed?.[DISTANCE] ||
  parsed?.locationCountryName ||
  parsed?.locationStateName,
);

const applyMutualExclusion = (baseFilters) => {
  if (baseFilters[EVENT_STATE]) {
    baseFilters[DATE_RANGE] = null;
    baseFilters[START_DATE] = null;
    baseFilters[END_DATE] = null;
  } else if (baseFilters[DATE_RANGE]) {
    baseFilters[EVENT_STATE] = null;
    baseFilters[START_DATE] = null;
    baseFilters[END_DATE] = null;
  } else if (baseFilters[START_DATE] || baseFilters[END_DATE]) {
    baseFilters[EVENT_STATE] = null;
    baseFilters[DATE_RANGE] = null;
  }
};

export const determineInitialFilters = (parsed, auth) => {
  const { user, feedPreferences } = auth || {};
  const hasQueryParam = Object.keys(parsed).length > 0 && parsed?.query;
  const hasExplicitDatesInUrl = Boolean(parsed?.startDate || parsed?.endDate || parsed?.dateRange);

  const baseFilters = {
    [TYPE]: EVENTS,
    [UTR_MIN]: minUtrValue,
    [UTR_MAX]: maxUtrValue,
    [UTR_TYPE]: VERIFIED,
    [UTR_TEAM_TYPE]: SINGLES,
    [TOURS]: {},
    [EVENT_TYPES]: {},
    [EVENT_UTR_TYPES]: {},
    [EVENT_TEAM_TYPES]: {},
    [GENDERS]: {},
    [BALL_TYPES]: {},
    [SPORT_TYPES]: buildSportTypes(user, feedPreferences),
  };

  if (!hasQueryParam && !hasExplicitDatesInUrl) {
    baseFilters[EVENT_STATE] = OPEN_REGISTRATION;
    if (!hasAnyLocationParam(parsed)) {
      applyLocationFromUser(baseFilters, user);
    }
  } else {
    baseFilters[UTR_MIN] = minUtrValue;
    baseFilters[UTR_MAX] = maxUtrValue;
    baseFilters[UTR_TYPE] = VERIFIED;
    QUERY_BEHAVIOR.FIELDS_CLEARED_BY_QUERY.forEach(field => {
      baseFilters[field] = null;
    });
  }

  Object.assign(baseFilters, parseUrlParamsToFilters(parsed));

  applyMutualExclusion(baseFilters);

  return baseFilters;
};
